import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import type { CourseOutlet } from "./CourseHome";
import { PathTrailSkeleton } from "./LearningPath";

const COURSE_KEY = "acuity-student-course";

type Enrolled = { id: string; title: string };

function rememberCourse(id: string) {
  try {
    sessionStorage.setItem(COURSE_KEY, id);
  } catch {
    /* private mode */
  }
}

function lastCourseId() {
  try {
    return sessionStorage.getItem(COURSE_KEY) || "";
  } catch {
    return "";
  }
}

/**
 * /student/path and /student/grades have no class in the URL yet.
 * Open the last class this browser used, otherwise the first enrolled one.
 */
export function CourseJump({ section }: { section: "path" | "grades" }) {
  const navigate = useNavigate();
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const env = await api<{ success: boolean; data: Enrolled[] }>("/api/student/courses");
        if (cancel) return;
        const ids = (env.data || []).map((c) => c.id);
        const saved = lastCourseId();
        const id = ids.includes(saved) ? saved : ids[0];
        if (!id) {
          setEmpty(true);
          return;
        }
        rememberCourse(id);
        navigate(`/student/${section}/${id}`, { replace: true });
      } catch (err) {
        if (!cancel) setError(errorMessage(err));
      }
    })();
    return () => {
      cancel = true;
    };
  }, [navigate, section]);

  if (error) {
    return (
      <div className="student-page">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (empty) {
    return (
      <div className="student-page">
        <div className="student-empty">
          <div>
            <strong>No classes yet</strong>
            <p>
              {section === "grades"
                ? "When you are in a class, grades for that class show up here."
                : "When you are in a class, the path for that class shows up here."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <PathTrailSkeleton />;
}

/**
 * Path and Grades on the student rail. The class is in the URL; a switcher
 * appears only when the student is in more than one.
 */
export function StudentCourseShell({ section }: { section: "path" | "grades" }) {
  const { courseId = "" } = useParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Enrolled[]>([]);
  const [data, setData] = useState<CourseOutlet | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!courseId) return;
    setError("");
    setData(null);
    try {
      const [listEnv, overviewEnv] = await Promise.all([
        api<{ success: boolean; data: Enrolled[] }>("/api/student/courses"),
        api<{ success: boolean; data: CourseOutlet }>(
          `/api/student/courses/${encodeURIComponent(courseId)}`,
        ),
      ]);
      const list = listEnv.data || [];
      if (!list.some((c) => c.id === courseId)) {
        const saved = lastCourseId();
        const next = list.find((c) => c.id === saved)?.id || list[0]?.id;
        if (next) {
          navigate(`/student/${section}/${next}`, { replace: true });
          return;
        }
      }
      rememberCourse(courseId);
      setCourses(list.map((c) => ({ id: c.id, title: c.title })));
      setData(overviewEnv.data);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [courseId, navigate, section]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="student-page">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="student-page">
      <div className="student-page-head">
        <div style={{ minWidth: 0 }}>
          <span className="student-eyebrow">{section === "path" ? "Learning path" : "Grades"}</span>
          <h1>{data?.course.title || "Your class"}</h1>
          {section === "path" && data && data.path.totals.activitiesTotal > 0 && (
            <p>
              Work through one activity at a time.{" "}
              {data.path.totals.activitiesDone > 0 && (
                <strong style={{ color: "var(--s-green-d)" }}>
                  {data.path.totals.activitiesDone} of {data.path.totals.activitiesTotal} complete so
                  far.
                </strong>
              )}
            </p>
          )}
        </div>
      </div>
      {courses.length > 1 && (
        <label className="student-course-pick">
          <span className="student-course-pick__label">Class</span>
          <select
            value={courseId}
            onChange={(e) => {
              const next = e.target.value;
              if (next && next !== courseId) {
                navigate(`/student/${section}/${next}`);
              }
            }}
          >
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </label>
      )}
      {data ? <Outlet context={data} /> : <PathTrailSkeleton />}
    </div>
  );
}
